import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Generate a robust content fingerprint for change detection.
 * Uses dual hashing (DJB2 + DJB2a XOR variant) combined with content length
 * to minimize collision probability compared to a single 32-bit hash.
 */
const contentFingerprint = (str) => {
  if (!str) return '0-0-0';
  let hash1 = 0;
  let hash2 = 5381;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1) + char;
    hash1 = hash1 & hash1;
    hash2 = ((hash2 << 5) + hash2) ^ char;
    hash2 = hash2 & hash2;
  }
  return `${hash1}-${hash2}-${str.length}`;
};

/**
 * Generic hook for managing versions of any content type
 *
 * @param {Object} config Configuration object
 * @param {Function} config.extractVersions Function to extract versions from conversation
 * @param {Function} config.parseContent Function to parse content for each version
 * @param {Function} config.isRelevantMessage Function to determine if a message contains relevant content
 * @param {Object} conversation The conversation object
 * @param {boolean} isStreaming Whether content is currently streaming
 * @param {string} streamingContent The current streaming content
 */
const useVersionManagement = ({
  extractVersions,
  parseContent,
  isRelevantMessage,
  conversation,
  isStreaming,
  streamingContent
}) => {
  const [versions, setVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const lastConversationIdRef = useRef(null);
  const processedMessageIndicesRef = useRef(new Set());
  // Track content hashes to detect regeneration (content change at same index)
  const contentHashesRef = useRef(new Map());
  // Ref to coordinate version index updates without setTimeout race conditions
  const pendingVersionIndexRef = useRef(null);

  // Process conversation messages into versions
  useEffect(() => {
    if (!conversation?.messages) return;

    const currentConversationId = conversation._id || conversation.id;

    // Reset when conversation changes
    if (lastConversationIdRef.current !== currentConversationId) {
      console.log('🔄 Conversation changed, resetting versions');
      setVersions([]);
      setCurrentVersionIndex(0);
      processedMessageIndicesRef.current = new Set();
      contentHashesRef.current = new Map();
      lastConversationIdRef.current = currentConversationId;
      return; // Return early and let next render process messages
    }

    // Skip if still streaming - preview handles this
    if (isStreaming) {
      console.log('⏳ Skipping version processing - still streaming');
      return;
    }

    // Find all relevant assistant messages with their indices
    const relevantMessages = [];
    console.log('conversation.messages', conversation.messages);
    conversation.messages.forEach((msg, originalIndex) => {
      if (msg.role === 'assistant' && isRelevantMessage(msg)) {
        console.log('originalIndex', originalIndex)
        relevantMessages.push({ msg, originalIndex });
      }
    });

    // Use module-level contentFingerprint for robust change detection

    // Build versions from relevant messages
    const newVersions = [];
    let hasContentChanged = false;

    // First pass: collect all versions and detect content changes
    // DO NOT update contentHashesRef here - do it after we've decided on versions
    const hashUpdates = new Map(); // Store hash updates to apply later

    relevantMessages.forEach(({ msg, originalIndex }) => {
      const contentHash = contentFingerprint(msg.content || '');
      const previousHash = contentHashesRef.current.get(originalIndex);

      // Also use timestamp to detect regeneration (message replaced at same index)
      const msgTimestamp = msg.createdAt || msg.updatedAt || '';
      const previousTimestamp = contentHashesRef.current.get(`ts-${originalIndex}`);

      console.log('🔍 Hash comparison at index', originalIndex, {
        contentHash,
        previousHash,
        hasExistingHash: !!previousHash,
        hashesMatch: previousHash === contentHash,
        msgTimestamp,
        previousTimestamp,
        timestampChanged: previousTimestamp && previousTimestamp !== msgTimestamp
      });

      // Check if content changed at this index (regeneration)
      // Detect by: different hash OR different timestamp (message was replaced)
      const contentChanged = previousHash && previousHash !== contentHash;
      const timestampChanged = previousTimestamp && previousTimestamp !== msgTimestamp;

      if (contentChanged || timestampChanged) {
        console.log('🔄 Content changed at index', originalIndex, '- regeneration detected', {
          contentChanged,
          timestampChanged
        });
        hasContentChanged = true;
      }

      // Use a unique ID that includes index, hash, AND timestamp
      // This ensures regenerated content (even with same hash) gets a new version
      const messageId = `msg-${originalIndex}-${contentHash}-${msgTimestamp}`;
      const parsedContent = parseContent(msg.content);

      if (parsedContent && (Array.isArray(parsedContent) ? parsedContent.length > 0 : true)) {
        const version = extractVersions
          ? extractVersions(msg, parsedContent, newVersions.length + 1)
          : {
              timestamp: msg.createdAt || new Date().toISOString(),
              content: msg.content,
              parsedContent: parsedContent,
              versionNumber: newVersions.length + 1
            };

        version.id = messageId;
        version.messageIndex = originalIndex;
        version.contentHash = contentHash;
        version.msgTimestamp = msgTimestamp;
        newVersions.push(version);

        // Store hash and timestamp updates to apply later
        hashUpdates.set(originalIndex, contentHash);
        hashUpdates.set(`ts-${originalIndex}`, msgTimestamp);
      }
    });

    // Apply hash updates AFTER we've built all versions
    hashUpdates.forEach((value, key) => {
      contentHashesRef.current.set(key, value);
    });

    if (newVersions.length === 0) {
      console.log('📭 No versions found in messages');
      return;
    }

    console.log('📦 New versions to process:', newVersions.map(v => ({
      id: v.id,
      messageIndex: v.messageIndex,
      contentHash: v.contentHash
    })));

    // Update versions state
    // For regeneration: keep old versions + add new regenerated version
    // For new messages: add new versions
    setVersions(prevVersions => {
      // Keep only non-preview versions for comparison
      const prevNonPreview = prevVersions.filter(v => !v.isPreview);

      // Check if we actually have new versions by comparing IDs
      const prevIds = new Set(prevNonPreview.map(v => v.id));
      const newIds = new Set(newVersions.map(v => v.id));

      // If same IDs, no changes needed
      const sameVersions = prevIds.size === newIds.size &&
        [...prevIds].every(id => newIds.has(id));

      if (sameVersions) {
        console.log('✅ Same versions detected, no update needed', {
          prevIds: Array.from(prevIds),
          newIds: Array.from(newIds),
          hasContentChanged
        });
        // Update processed indices
        newVersions.forEach(v => processedMessageIndicesRef.current.add(v.messageIndex));
        return prevNonPreview;
      }

      // For regeneration: merge previous versions with new versions
      // Keep all previous versions (they represent historical regenerations)
      // Add any new versions that don't already exist
      let mergedVersions;

      if (hasContentChanged) {
        // Content at an existing index changed (regeneration)
        // Keep all previous versions and add the new regenerated version
        console.log('🔄 Regeneration detected - adding new version while keeping history');

        // Filter out versions at the same messageIndex with different hash (old regenerated content at same index)
        // We want to keep all historical versions
        const existingVersions = prevNonPreview;
        const newUniqueVersions = newVersions.filter(nv => !prevIds.has(nv.id));

        mergedVersions = [...existingVersions, ...newUniqueVersions];
      } else {
        // Normal case: new message added
        const uniqueNewVersions = newVersions.filter(nv => !prevIds.has(nv.id));
        mergedVersions = [...prevNonPreview, ...uniqueNewVersions];
      }

      console.log('🆕 Updating versions:', {
        prevCount: prevNonPreview.length,
        newCount: mergedVersions.length,
        hasContentChanged
      });

      // Sort by timestamp
      const sortedVersions = mergedVersions.sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      // Re-number versions
      sortedVersions.forEach((version, index) => {
        version.versionNumber = index + 1;
      });

      // Update processed indices
      sortedVersions.forEach(v => processedMessageIndicesRef.current.add(v.messageIndex));

      // Schedule index update via ref — applied by useEffect after versions state commits
      pendingVersionIndexRef.current = sortedVersions.length - 1;

      return sortedVersions;
    });

  // Note: extractVersions, parseContent, isRelevantMessage are intentionally excluded from deps
  // because they are inline functions that get recreated each render but have stable behavior
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.messages, conversation?._id, conversation?.id, isStreaming]);

  // Handle streaming preview
  useEffect(() => {
    if (!isStreaming || !streamingContent) {
      // Clean up preview when streaming ends
      setVersions(prev => {
        const nonPreview = prev.filter(v => !v.isPreview);
        if (nonPreview.length !== prev.length) {
          console.log('🧹 Cleaned up preview version');
        }
        return nonPreview;
      });
      return;
    }

    // Create preview version for streaming content
    const streamingMessage = { content: streamingContent, role: 'assistant' };
    if (!isRelevantMessage(streamingMessage)) return;

    const parsedContent = parseContent(streamingContent);
    if (!parsedContent || (Array.isArray(parsedContent) && parsedContent.length === 0)) return;

    setVersions(prevVersions => {
      const nonPreviewVersions = prevVersions.filter(v => !v.isPreview);

      const previewVersion = extractVersions
        ? extractVersions(streamingMessage, parsedContent, nonPreviewVersions.length + 1, true)
        : {
            timestamp: new Date().toISOString(),
            content: streamingContent,
            parsedContent: parsedContent,
            versionNumber: nonPreviewVersions.length + 1,
            isPreview: true,
            label: 'Generating...'
          };

      // Always use consistent preview ID
      previewVersion.id = 'preview-streaming';
      previewVersion.isPreview = true;

      console.log('👁️ Adding/updating preview version');
      const updated = [...nonPreviewVersions, previewVersion];
      // Schedule index update via ref — applied by useEffect after versions state commits
      pendingVersionIndexRef.current = updated.length - 1;
      return updated;
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, streamingContent]);

  // Apply any pending version index changes after versions state commits.
  // This replaces setTimeout-based index updates which caused race conditions.
  useEffect(() => {
    if (pendingVersionIndexRef.current !== null) {
      setCurrentVersionIndex(pendingVersionIndexRef.current);
      pendingVersionIndexRef.current = null;
    }
  }, [versions]);

  const currentVersion = versions[currentVersionIndex] || null;

  // Helper function to add a new version manually
  const addVersion = useCallback((content, parsedContent, metadata = {}) => {
    const newVersion = {
      id: `manual-${Date.now()}`,
      timestamp: new Date().toISOString(),
      content: content,
      parsedContent: parsedContent,
      isManual: true,
      ...metadata
    };

    setVersions(prev => {
      newVersion.versionNumber = prev.length + 1;
      const updated = [...prev, newVersion];
      pendingVersionIndexRef.current = updated.length - 1;
      return updated;
    });

    return newVersion;
  }, []);

  // Helper function to update current version
  const updateCurrentVersion = useCallback((updates) => {
    if (!currentVersion || currentVersion.isPreview) return;

    setVersions(prev => prev.map(v =>
      v.id === currentVersion.id ? { ...v, ...updates } : v
    ));
  }, [currentVersion]);

  return {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions: versions.length > 0,
    isLatestVersion: currentVersionIndex === versions.length - 1,
    addVersion,
    updateCurrentVersion
  };
};

export default useVersionManagement;
